import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { getLabel } from '@/i18n/maps';
import type { FloatingButtonProps } from '@/types/widget';
import { openInNewTab } from '@/utils/url-template';
import { clampToViewport, computeInitialPosition, reanchorToNearestEdges } from './drag-math';
import { loadPosition, savePosition, type Position } from './position-storage';
import { useDraggable, viewportSize } from './useDraggable';
import { ART } from '@/art';

// The advisor (who waves on hover) is for a wide screen with a mouse; phones and tablets
// (an iPad too, either way round) and narrower windows get the round chat button. The same
// query as the .cal-fab rules in styles/index.css, so "desktop" means the same thing here.
const DESKTOP_FAB_QUERY = '(min-width: 1280px) and (hover: hover) and (pointer: fine)';
const isDesktopFab = (): boolean =>
  typeof window.matchMedia === 'function'
    ? window.matchMedia(DESKTOP_FAB_QUERY).matches
    : window.innerWidth >= 1280; // no matchMedia (tests): by width alone
// Desktop gets more breathing room above the bottom edge; on a short phone
// viewport 80px would push the button conspicuously far up the screen.
const DESKTOP_BOTTOM_GAP_PX = 80;
const MOBILE_BOTTOM_GAP_PX = 24;

function defaultInitialPosition(): {
  edge: 'right';
  offset: string;
  anchorY: 'bottom';
} {
  const gap = isDesktopFab() ? DESKTOP_BOTTOM_GAP_PX : MOBILE_BOTTOM_GAP_PX;
  return { edge: 'right', offset: `${gap}px`, anchorY: 'bottom' };
}
// Pre-paint size estimate for the initial position, before the element exists to
// measure: the box, which the art overflows on both sides so it tucks behind the
// docked edge — 35×55 for the round button (55px disc), 188×220 for the advisor. Keep
// in sync with .cal-fab in styles/index.css. Both are fixed sizes, so the estimate is exact.
const PHONE_BUTTON_SIZE = { width: 35, height: 55 };
const DESKTOP_BUTTON_SIZE = { width: 188, height: 220 };
const buttonSize = () => (isDesktopFab() ? DESKTOP_BUTTON_SIZE : PHONE_BUTTON_SIZE);


/** The chat page, told which page the visitor opened it from (`?from=`, for tracking). */
function chatUrlFrom(chatPageUrl: string): string {
  const url = new URL(chatPageUrl, window.location.href);
  url.searchParams.set('from', window.location.href);
  return url.toString();
}

export function FloatingButton(props: FloatingButtonProps): JSX.Element {
  const locale = props.locale ?? 'fr';
  const label = getLabel('floating_button_label', locale);
  // The default position applies on a first visit only; after that the
  // user's own dragged position is restored. Hosts can opt out entirely.
  const persistPosition = props.persistPosition ?? true;

  const initialPosition = useMemo<Position>(() => {
    if (typeof window === 'undefined') return { x: 0, y: 0 };
    const viewport = viewportSize();
    const size = buttonSize();

    if (persistPosition) {
      const stored = loadPosition();
      if (stored) {
        // Re-derive the anchor rather than trusting the raw coordinates: a
        // position saved at 1920px wide means "hugging the right edge", and
        // replaying it verbatim at 2400px leaves the button stranded in the
        // middle — the same one-directional-clamp bug the resize handler
        // exists to avoid, just across page loads instead of within one.
        if (stored.vw != null && stored.vh != null && stored.w != null && stored.h != null) {
          return reanchorToNearestEdges(
            stored,
            { viewport: { width: stored.vw, height: stored.vh }, size: { width: stored.w, height: stored.h } },
            { viewport, size },
          );
        }
        // Written by an older build with no capture geometry: clamping is all
        // we can safely do, and still keeps the button on-screen.
        return clampToViewport(stored, viewport, size);
      }
    }
    // Resolved here rather than at module scope: the default gap depends on
    // the viewport, which only exists at mount.
    const spec = props.initialPosition ?? defaultInitialPosition();
    return computeInitialPosition(spec, viewport, size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  function navigate() {
    if (props.onOpenChat) {
      props.onOpenChat();
    } else if (props.chatPageUrl) {
      openInNewTab(chatUrlFrom(props.chatPageUrl));
    } else {
      console.warn(
        '[CalebasseChatbot] mountFloatingButton: neither chatPageUrl nor onOpenChat provided',
      );
    }
  }

  const drag = useDraggable({
    initialPosition,
    onClick: navigate,
    onPositionChange: persistPosition
      ? (pos, geometry) =>
          savePosition(pos, {
            vw: geometry.viewport.width,
            vh: geometry.viewport.height,
            w: geometry.size.width,
            h: geometry.size.height,
          })
      : undefined,
  });

  if (!mounted) return <></>;

  // Wrapper is position:fixed at 0,0 — the hook moves it via transform.
  // No left/top so React never fights the direct DOM mutations during drag.
  const wrapperStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 42,
    cursor: drag.isDragging ? 'grabbing' : 'grab',
    touchAction: 'none',
    userSelect: 'none',
    willChange: 'transform',
  };

  return (
    <div
      ref={drag.bindToElement}
      className="calebasse-chatbot-root gtmtrack"
      content-content="chatbot-fab-ouvrir"
      style={wrapperStyle}
    >
      <button
        type="button"
        aria-label={label}
        title={label}
        className="cal-fab"
        style={{ cursor: 'inherit' }}
      >
        {/* CSS shows the round button on phones and tablets, the resting/waving advisor on desktop */}
        <span className="cal-fab-art" aria-hidden="true">
          {/* phone: a glowing copy behind the art, its opacity "breathes" */}
          <img className="cal-fab-glow" src={ART.fabPhone} alt="" draggable={false} loading="lazy" />
          <img className="cal-fab-phone" src={ART.fabPhone} alt="" draggable={false} loading="lazy" />
          <img className="cal-fab-rest" src={ART.fabRest} alt="" draggable={false} loading="lazy" />
          <img className="cal-fab-hover" src={ART.fabHover} alt="" draggable={false} loading="lazy" />
        </span>
      </button>
    </div>
  );
}
